// Rol şablonu oluştur / düzenle drawer'ı. Ad + kod (DrawerField) ve
// domain'e göre gruplanmış izin kutuları. Silme bu drawer'ın gövdesinin
// altında durur (varsayılan şablon silinemez).

import React from "react";
import { Box, CircularProgress, Drawer, IconButton, Tooltip, Typography, alpha } from "@mui/material";
import {
  Shield as ShieldIcon,
  ContentCopy as CopyIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import { ActionButton, DrawerFooter, DrawerHeader, FormCheckbox, FormTextField } from "../../../../components/atoms";
import { DrawerField, tileInputProps } from "../../../../components/molecules";
import { StatusPill } from "../../../../components/organisms/ClinicRecordList/recordTableParts";
import { COLORS, TYPO } from "../../../../styles/uiConstants";
import { DOMAIN_LABELS } from "./staffFormat";

const paperSx = {
  width: { xs: "100%", sm: 640 },
  bgcolor: COLORS.white,
  boxShadow: "0 8px 32px 0 rgba(60,72,100,0.15)",
  display: "flex",
  flexDirection: "column",
};

export default function RoleEditorDrawer({
  open,
  mode = "create",
  role,
  name,
  onNameChange,
  permissions,
  groupedPermissions,
  onTogglePermission,
  onToggleDomain,
  canEdit = true,
  canDelete = false,
  onDelete,
  onCopyCode,
  onClose,
  onSubmit,
  loading,
  palette,
}) {
  const isEdit = mode === "edit";
  const isDefault = isEdit && Boolean(role?.isDefault);
  const editable = !isEdit || canEdit;
  const groups = Object.entries(groupedPermissions);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: paperSx }}>
      <DrawerHeader
        icon={<ShieldIcon />}
        title={isEdit ? role?.name || "Rol Şablonu" : "Yeni Rol Şablonu"}
        subtitle={editable ? "Şablonun izinlerini belirleyin" : "Yalnızca görüntüleme"}
        onClose={onClose}
      />

      <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: COLORS.white, flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Box sx={{ display: "grid", rowGap: 2.5, columnGap: 2, gridTemplateColumns: { xs: "1fr", sm: isEdit && role?.code ? "1.6fr 1fr" : "1fr" } }}>
          <DrawerField
            label={editable && !isDefault ? "Şablon Adı *" : "Şablon Adı"}
            hint={isDefault ? "Varsayılan şablonun adı değiştirilemez." : undefined}
          >
            {editable && !isDefault ? (
              <FormTextField
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="örn: Doktor, Sekreter..."
                fullWidth size="small" variant="standard"
                inputProps={{ "aria-label": "Şablon Adı" }}
                InputProps={tileInputProps(true)}
              />
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                <Typography sx={{ fontSize: "0.875rem", color: COLORS.slate800 }}>{name}</Typography>
                {isDefault ? <StatusPill label="Varsayılan" tone="warning" /> : null}
              </Box>
            )}
          </DrawerField>

          {isEdit && role?.code ? (
            <DrawerField label="Şablon Kodu">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.25 }}>
                <Typography sx={{ fontFamily: "'SF Mono', 'Fira Code', monospace", fontSize: "0.875rem", color: palette.primary, letterSpacing: 1 }}>
                  {role.code}
                </Typography>
                <Tooltip title="Kodu kopyala" arrow>
                  <IconButton size="small" aria-label="Kodu kopyala" onClick={() => onCopyCode(role.code)} sx={{ width: 26, height: 26 }}>
                    <CopyIcon sx={{ fontSize: 13, color: COLORS.slate400 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </DrawerField>
          ) : null}
        </Box>

        {/* İzinler */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 3, mb: 1.25 }}>
          <Typography sx={{ fontSize: TYPO.label, fontWeight: 600, color: COLORS.slate500 }}>İzinler</Typography>
          <StatusPill label={`${permissions.length} seçili`} tone="primary" palette={palette} />
        </Box>

        {groups.length === 0 ? (
          <Box sx={{ py: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} sx={{ color: COLORS.slate300 }} />
            <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate400 }}>İzinler yükleniyor...</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            {groups.map(([domain, perms]) => {
              const keys = perms.map((p) => p.key);
              const allSelected = keys.every((k) => permissions.includes(k));
              const selectedCount = keys.filter((k) => permissions.includes(k)).length;
              return (
                <Box
                  key={domain}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${allSelected ? alpha(palette.primary, 0.3) : COLORS.slate200}`,
                    overflow: "hidden",
                    bgcolor: COLORS.white,
                  }}
                >
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.25,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      bgcolor: allSelected ? alpha(palette.primary, 0.04) : COLORS.slate50,
                      borderBottom: `1px solid ${COLORS.slate100}`,
                    }}
                  >
                    <FormCheckbox
                      size="small"
                      palette={palette}
                      checked={allSelected}
                      indeterminate={selectedCount > 0 && !allSelected}
                      disabled={!editable}
                      onChange={() => onToggleDomain(domain, perms)}
                      label={
                        <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }}>
                          {DOMAIN_LABELS[domain] || domain}
                        </Typography>
                      }
                    />
                    <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate400, fontVariantNumeric: "tabular-nums" }}>
                      {selectedCount}/{perms.length}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 1 }}>
                    {perms.map((perm, idx) => {
                      const checked = permissions.includes(perm.key);
                      return (
                        <Box
                          key={perm.key}
                          sx={{
                            px: 0.5,
                            borderBottom: idx < perms.length - 1 ? `1px solid ${COLORS.slate50}` : "none",
                          }}
                        >
                          <FormCheckbox
                            size="small"
                            palette={palette}
                            fullWidth
                            checked={checked}
                            disabled={!editable}
                            onChange={() => onTogglePermission(perm.key)}
                            label={
                              <Typography sx={{ fontSize: TYPO.label, color: checked ? COLORS.slate800 : COLORS.slate500 }}>
                                {perm.description || perm.key}
                              </Typography>
                            }
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Silme — yalnız mevcut, varsayılan olmayan şablonda */}
        {isEdit && canDelete ? (
          <Box
            sx={{
              mt: 4,
              pt: 2.5,
              borderTop: `1px solid ${COLORS.slate100}`,
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }}>Şablonu sil</Typography>
              <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>
                {isDefault ? "Varsayılan şablon silinemez." : "Şablon kalıcı olarak silinir."}
              </Typography>
            </Box>
            <ActionButton
              variant="delete"
              compact
              tone="soft"
              palette={palette}
              label="Sil"
              icon={<DeleteIcon sx={{ fontSize: 15 }} />}
              disabled={isDefault}
              onClick={onDelete}
            />
          </Box>
        ) : null}
      </Box>

      {editable ? (
        <DrawerFooter
          onCancel={onClose}
          cancelLabel="Vazgeç"
          onSubmit={onSubmit}
          submitLabel={isEdit ? "Güncelle" : "Oluştur"}
          loading={loading}
          disabled={!name.trim()}
          startIcon={<ShieldIcon sx={{ fontSize: 15 }} />}
          fullWidth
        />
      ) : (
        <DrawerFooter onCancel={onClose} cancelLabel="Kapat" fullWidth />
      )}
    </Drawer>
  );
}
